/*
  Выпадающий список кабинета.

  07.09.2026, решение владельца - тот же приём, что в Арбитр ДА с 20.08.2026.
  Родное поле браузера нельзя ни оформить, ни анимировать: раскрытый список
  рисует не страница, а сам браузер. Посреди оформленного кабинета он выглядел
  голым html, а стрелка не показывала, раскрыт список или нет.

  На узком экране (телефон, планшет) остаётся РОДНОЕ поле: там браузер
  показывает список колесом во весь экран, и своя разметка была бы хуже -
  мелкие строки, по которым неудобно попадать пальцем. Граница та же, что у
  остального кабинета: 900 пикселей (`styles.css`).

  Разметка соответствует роли `combobox` + `listbox`: читалка называет поле,
  говорит, раскрыт ли список, и озвучивает выбранную строку. Родное поле даёт
  это даром, своя разметка - только если про это помнить.
*/

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'

import useReveal from './useReveal.js'

const COMPACT = '(max-width: 900px)'

/** Узкий экран - тот же порог, что у раскладки кабинета. */
function useIsCompact() {
  const [compact, setCompact] = useState(() => window.matchMedia(COMPACT).matches)
  useEffect(() => {
    const query = window.matchMedia(COMPACT)
    const listen = (event) => setCompact(event.matches)
    query.addEventListener('change', listen)
    return () => query.removeEventListener('change', listen)
  }, [])
  return compact
}

/** Строки списка: пустая строка первой, если она заведена. */
function useItems(options, empty) {
  return useMemo(() => {
    const rows = Object.entries(options).map(([key, label]) => ({ value: key, label }))
    return empty ? [{ value: '', label: empty }, ...rows] : rows
  }, [options, empty])
}

/*
  Стрелка вынесена именованным экспортом 14.09.2026: её же показывает поле с
  ручным вводом (`CourtField.jsx`). Две копии одной стрелки разъехались бы на
  первой правке оформления, а человек читает их как один и тот же элемент.
*/
export function Arrow({ open }) {
  return (
    <svg
      className={`select__arrow${open ? ' select__arrow--open' : ''}`}
      viewBox="0 0 16 16"
      aria-hidden="true"
    >
      <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  )
}

export function Check({ shown }) {
  return (
    <svg
      className={`select__check${shown ? '' : ' select__check--hidden'}`}
      viewBox="0 0 16 16"
      aria-hidden="true"
    >
      <path d="M3 8.5l3.5 3.5L13 5" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  )
}

export default function Select({
  value,
  onChange,
  options,
  empty,
  disabled = false,
  // Разметка своего списка - кнопка, а не поле, и обёртка `<label>` его не
  // называет. Читалке имя даётся ссылкой на подпись: `labelledBy`.
  labelledBy,
}) {
  const compact = useIsCompact()
  const items = useItems(options, empty)
  const props = { value: value ?? '', onChange, items, disabled, labelledBy }
  return compact ? <NativeSelect {...props} /> : <CustomSelect {...props} />
}

/** Родное поле - для телефона. Стрелку рисуем свою, чтобы вид не отличался. */
function NativeSelect({ value, onChange, items, disabled, labelledBy }) {
  return (
    <span className="select">
      <select
        aria-labelledby={labelledBy}
        className="field__select select__native"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        {items.map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </select>
      <Arrow open={false} />
    </span>
  )
}

function CustomSelect({ value, onChange, items, disabled, labelledBy }) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const root = useRef(null)
  const trigger = useRef(null)
  const listId = useId()
  // Список остаётся в разметке, пока идёт исчезновение, и получает классы
  // перехода только после первой отрисовки - `useReveal.js`.
  const { mounted, shown } = useReveal(open)

  // Нажатие мимо поля закрывает список - как у родного.
  useEffect(() => {
    if (!open) return undefined
    const outside = (event) => {
      if (!root.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', outside)
    return () => document.removeEventListener('mousedown', outside)
  }, [open])

  const openList = useCallback(() => {
    if (disabled) return
    const current = items.findIndex((item) => item.value === value)
    setActive(current >= 0 ? current : 0)
    setOpen(true)
  }, [disabled, items, value])

  const choose = useCallback(
    (index) => {
      const item = items[index]
      if (!item) return
      onChange(item.value)
      setOpen(false)
      trigger.current?.focus()
    },
    [items, onChange],
  )

  const onKeyDown = useCallback(
    (event) => {
      if (disabled) return
      if (!open) {
        if ([' ', 'Enter', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
          event.preventDefault()
          openList()
        }
        return
      }
      if (event.key === 'Escape' || event.key === 'Tab') {
        setOpen(false)
        // Фокус возвращаем только при Escape: Tab обязан уйти дальше по форме.
        if (event.key === 'Escape') {
          trigger.current?.focus()
          // Esc принадлежит раскрытому списку: окно вокруг него остаётся и не
          // теряет введённое (аудит 26.10.2026, причина 3). `Modal` слушает
          // `window` и пропускает событие, у которого стоит `defaultPrevented`.
          event.preventDefault()
          event.stopPropagation()
        }
        return
      }
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault()
        choose(active)
        return
      }
      const moves = {
        ArrowDown: () => Math.min(active + 1, items.length - 1),
        ArrowUp: () => Math.max(active - 1, 0),
        Home: () => 0,
        End: () => items.length - 1,
      }
      const move = moves[event.key]
      if (move) {
        event.preventDefault()
        setActive(move())
      }
    },
    [active, choose, disabled, items.length, open, openList],
  )

  const chosen = items.find((item) => item.value === value)

  return (
    <span className="select" ref={root}>
      <button
        ref={trigger}
        type="button"
        role="combobox"
        aria-labelledby={labelledBy}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
        className="field__select select__button no-hover-lift"
      >
        <span className="select__label">{chosen ? chosen.label : ''}</span>
      </button>
      <Arrow open={open} />

      {mounted ? (
        <ul
          id={listId}
          role="listbox"
          tabIndex={-1}
          onKeyDown={onKeyDown}
          className={`select__list${shown ? ' select__list--shown' : ''}`}
        >
          {items.map((item, index) => (
            <li
              key={item.value}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={item.value === value}
              onMouseEnter={() => setActive(index)}
              /*
                `preventDefault` здесь обязателен (08.09.2026, найдено осмотром
                выбора периода на отчётах). Поле почти всегда стоит внутри
                `<label>` (`Field` в `Common.jsx`), а подпись по клику внутри
                себя отправляет ВТОРОЙ клик на свой элемент управления - на нашу
                кнопку. Список успевал закрыться от выбора и тут же открывался
                снова: значение менялось, а список висел раскрытым поверх
                страницы. Отменённое событие активационное поведение подписи не
                выполняет.
              */
              onClick={(event) => {
                event.preventDefault()
                choose(index)
              }}
              className={`select__option${index === active ? ' select__option--active' : ''}`}
            >
              <Check shown={item.value === value} />
              <span className="select__label">{item.label}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </span>
  )
}
